/**
 * navigationGuards.js
 *
 * Guard de navegação do Vue Router.
 * Usa Firebase para verificar sessão e o backend para checar role.
 */
import { getCurrentUser }                                              from '@/services/authService'
import { isUserAdmin, isUserDev, verificarStatusCadastro, getUserData } from '@/services/userService'

// ── Loading global ────────────────────────────────────────────────
function showLoading() {
  if (document.getElementById('route-loading')) return
  const el = document.createElement('div')
  el.id = 'route-loading'
  el.innerHTML = `
    <div style="
      position: fixed; inset: 0;
      background: #080d1a;
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      z-index: 9999;
    ">
      <div style="
        width: 40px; height: 40px;
        border: 3px solid rgba(255,255,255,0.1);
        border-top-color: #198754;
        border-radius: 50%;
        animation: spin 0.7s linear infinite;
      "></div>
      <p style="color: #6c757d; font-family: sans-serif; font-size: 13px; margin-top: 16px;">
        Carregando…
      </p>
    </div>
    <style>
      @keyframes spin { to { transform: rotate(360deg); } }
    </style>
  `
  document.body.appendChild(el)
}

function hideLoading() {
  const el = document.getElementById('route-loading')
  if (el) el.remove()
}

// ── Guard principal ───────────────────────────────────────────────
export async function authGuard(to, from, next) {
  showLoading()

  try {
    const user = await getCurrentUser()

    // Na carga inicial (F5 / acesso direto por URL), força refresh do token
    // para que getIdTokenResult() devolva os custom claims mais recentes
    const isInitialLoad = !from.name

    const requiresAuth  = to.matched.some(r => r.meta.requiresAuth)
    const requiresGuest = to.matched.some(r => r.meta.requiresGuest)
    const requiresAdmin = to.matched.some(r => r.meta.requiresAdmin)
    const requiresDev   = to.matched.some(r => r.meta.requiresDev)
    // Rota acessível por aluno E admin (ex: Sobre) — sem isso, toda rota
    // com requiresAuth mas sem requiresAdmin é tratada como exclusiva de
    // aluno e forçaria redirect de qualquer admin pra dashAdmin.
    const isShared       = to.matched.some(r => r.meta.shared)

    // Rota protegida sem usuário logado → login
    if (requiresAuth && !user) {
      return next({ name: 'login' })
    }

    // Rota protegida com usuário logado → verificar role e depois cadastro
    if (requiresAuth && user && to.name !== 'completarCadastro') {
      try {
        // Role vem primeiro: admin/dev não tem student e nunca deve ir para completarCadastro
        const userData = await getUserData(user, isInitialLoad)
        const isPrivileged = userData?.isPrivileged === true

        // Admin Master criado automaticamente (nunca passou pelo cadastro de
        // aluno) precisa completar os dados mínimos de identificação (nome,
        // CPF, telefone) antes de usar qualquer outra parte do sistema —
        // mesmo princípio do aluno com cadastro incompleto, aplicado aqui só
        // ao ADMIN_MASTER (único role que hoje pode existir sem Person).
        if (userData?.role === 'ADMIN_MASTER' && !userData?.personId && to.name !== 'AdminPerfil') {
          sessionStorage.setItem('aerj_perfil_incompleto_redirect', '1')
          return next({ name: 'AdminPerfil' })
        }

        if (!requiresAdmin && !isShared && isPrivileged) return next({ name: 'dashAdmin' })

        // Só verifica cadastro para alunos comuns
        if (!isPrivileged) {
          try {
            const status = await verificarStatusCadastro(user)
            if (status === 'INCOMPLETO') return next({ name: 'completarCadastro' })
            if ((status === 'PENDENTE' || status === 'RETORNADO' || status === 'REJEITADO')
                && to.name !== 'aguardandoAprovacao') {
              return next({ name: 'aguardandoAprovacao' })
            }
          } catch {
            // Backend offline: não bloqueia acesso
          }
        }
      } catch {
        // Backend offline: deixa o usuário continuar para a rota solicitada
      }
    }

    // Usuário logado tentando acessar rota de convidado → redireciona para o dash certo
    if (requiresGuest && user) {
      try {
        const userData = await getUserData(user, isInitialLoad)
        if (userData?.isPrivileged) return next({ name: 'dashAdmin' })
        return next({ name: 'dashAlunos' })
      } catch {
        // Backend offline: redireciona para dash do aluno como fallback seguro
        return next({ name: 'dashAlunos' })
      }
    }

    // Rota exclusiva de admin
    if (requiresAdmin && user) {
      const admin = await isUserAdmin(user)
      if (!admin) return next({ name: 'dashAlunos' })
    }

    // Rota exclusiva de dev
    if (requiresDev && user) {
      const dev = await isUserDev(user)
      if (!dev) return next({ name: 'dashAlunos' })
    }

    next()

  } catch (error) {
    console.error('[authGuard] Erro:', error)
    // Evita loop infinito: se já está indo para login, deixa passar
    if (to.name === 'login') return next()
    next({ name: 'login' })
  } finally {
    hideLoading()
  }
}